import {
  Category,
  Role,
  roleFromJSON,
  roleToJSON,
} from "protos/robot_syncer/plant_profile";
import { CategoryChipPreviewBar } from "./CategoryChipPreviewBar";
import { CategoryNameAndRoleFields } from "./CategoryNameAndRoleFields";
import { categoryNameAndRoleSchema } from "./useCreateCategoryForm";
import {
  categoryOwnershipFromPlantProfile,
  UnsavedPlantProfile,
} from "portal/utils/categoryCollection/plantProfile";
import { CreateCategoryDialogActions } from "./CreateCategoryDialogActions";
import {
  DEFAULT_CREATE_CATEGORY_ROLE,
  type PlantProfileRoleOption,
} from "portal/utils/categoryCollection/category";
import { Form, Formik } from "formik";
import { object } from "yup";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import React, { FC } from "react";

type CopyFromTemplateConfigureValues = {
  name: string;
  role: PlantProfileRoleOption;
};

type CopyFromTemplateConfigureStepProps = {
  source: Category;
  defaultRole?: Role;
  unsavedPlantProfile: UnsavedPlantProfile;
  ineligibleChipIds: ReadonlySet<string>;
  selectedChipIds: ReadonlySet<string>;
  onToggleChip: (chipId: string, selected: boolean) => void;
  onAdd: (category: Category, role: Role) => void;
  onCancel: () => void;
  onBack: () => void;
};

export const CopyFromTemplateConfigureStep: FC<
  CopyFromTemplateConfigureStepProps
> = ({
  source,
  defaultRole,
  unsavedPlantProfile,
  ineligibleChipIds,
  selectedChipIds,
  onToggleChip,
  onAdd,
  onCancel,
  onBack,
}) => {
  const { t } = useTranslation();
  const chipIds: string[] = [];
  const seenChipIds = new Set<string>();
  for (const id of source.chipIds) {
    if (
      !id ||
      seenChipIds.has(id) ||
      ineligibleChipIds.has(id) ||
      !selectedChipIds.has(id)
    ) {
      continue;
    }
    seenChipIds.add(id);
    chipIds.push(id);
  }

  return (
    <Formik<CopyFromTemplateConfigureValues>
      initialValues={{
        name: source.name,
        role: defaultRole
          ? roleToJSON(defaultRole)
          : DEFAULT_CREATE_CATEGORY_ROLE,
      }}
      enableReinitialize
      validationSchema={object(categoryNameAndRoleSchema(t))}
      onSubmit={({ name, role }) => {
        if (chipIds.length === 0) {
          return;
        }
        onAdd(
          {
            id: uuid(),
            name: name.trim(),
            chipIds,
            almanac: source.almanac
              ? structuredClone(source.almanac)
              : undefined,
            ...categoryOwnershipFromPlantProfile(unsavedPlantProfile),
          },
          roleFromJSON(role)
        );
      }}
    >
      {({
        values,
        submitForm,
        isSubmitting,
        isValid,
        setFieldValue,
        setFieldTouched,
      }) => (
        <Form>
          <div className="flex flex-col gap-4 mt-2">
            <CategoryNameAndRoleFields
              role={values.role}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
            />
            <div className="flex flex-col gap-1">
              <Typography variant="body2" color="text.secondary">
                {t(
                  "components.categoryCollectionProfile.actions.selectedImages",
                  { count: chipIds.length }
                )}
              </Typography>
              <CategoryChipPreviewBar
                chipIds={source.chipIds}
                categoryId={source.id}
                ineligibleChipIds={ineligibleChipIds}
                selectedChipIds={selectedChipIds}
                onToggleChip={onToggleChip}
              />
            </div>
          </div>
          <CreateCategoryDialogActions
            onBack={onBack}
            onCancel={onCancel}
            onConfirm={submitForm}
            confirmLabel={t("utils.actions.add")}
            confirmDisabled={!isValid || chipIds.length === 0}
            confirmLoading={isSubmitting}
            disabled={isSubmitting}
          />
        </Form>
      )}
    </Formik>
  );
};
